const enum VideoPlayingState
{
    Initial,
    Playing,
    Paused,
    Played,
}

interface VideoEventListeners
{
    onPlayClick: EventListener;
    onPauseClick: EventListener;
    onVideoPlaying: EventListener;
    onVideoPaused: EventListener;
    onVideoPlayed: EventListener;
}

export class VideoControlService
{
    private readonly rootElement: HTMLElement;
    private readonly eventListeners: VideoEventListeners;
    private readonly videoElement: HTMLVideoElement;
    private readonly playButtonElement: HTMLElement | undefined;
    private readonly pauseButtonElement: HTMLElement | undefined;
    private readonly replayButtonElement: HTMLElement | undefined;

    private eventSubscription: boolean;
    private playingState: VideoPlayingState;

    constructor(rootElement: HTMLElement)
    {
        this.rootElement = rootElement;

        const videoElement = rootElement.querySelector<HTMLVideoElement>(":scope video");
        if (videoElement === null)
        {
            throw new Error("Video element not found.");
        }

        this.videoElement = videoElement;
        this.playButtonElement = rootElement.querySelector<HTMLElement>(":scope [data-video-play]") ?? undefined;
        this.pauseButtonElement = rootElement.querySelector<HTMLElement>(":scope [data-video-pause]") ?? undefined;
        this.replayButtonElement = rootElement.querySelector<HTMLElement>(":scope [data-video-replay]") ?? undefined;

        this.playingState = VideoPlayingState.Initial;
        this.PlayingState = videoElement.ended ? VideoPlayingState.Played : videoElement.paused ? VideoPlayingState.Paused : VideoPlayingState.Playing;

        this.eventListeners = {
            onPlayClick: this.handlePlayClick.bind(this) as EventListener,
            onPauseClick: this.handlePauseClick.bind(this) as EventListener,
            onVideoPlaying: this.handleVideoPlaying.bind(this) as EventListener,
            onVideoPaused: this.handleVideoPaused.bind(this) as EventListener,
            onVideoPlayed: this.handleVideoPlayed.bind(this) as EventListener,
        };
        this.eventSubscription = false;
        this.subscribe();
    }

    public set PlayingState(playingState: VideoPlayingState)
    {
        if (this.playingState !== playingState)
        {
            this.setButtonActive(this.playButtonElement, playingState === VideoPlayingState.Paused);
            this.setButtonActive(this.pauseButtonElement, playingState === VideoPlayingState.Playing);
            this.setButtonActive(this.replayButtonElement, playingState === VideoPlayingState.Played);
            this.playingState = playingState;
        }
    }

    public dispose(): void
    {
        this.unsubscribe();
    }

    private handlePlayClick(): void
    {
        this.videoElement.play();
    }

    private handlePauseClick(): void
    {
        this.videoElement.pause();
    }

    private handleVideoPlaying(): void
    {
        console.log("Playing");
        this.PlayingState = VideoPlayingState.Playing;
    }

    private handleVideoPaused(): void
    {
        console.log("Paused");
        this.PlayingState = VideoPlayingState.Paused;
    }

    private handleVideoPlayed(): void
    {
        console.log("Played");
        this.PlayingState = VideoPlayingState.Played;
    }

    private setButtonActive(buttonElement: HTMLElement | undefined, active: boolean)
    {
        if (buttonElement !== undefined)
        {
            if (active)
            {
                buttonElement.dataset.active = "";
            }
            else
            {
                delete buttonElement.dataset.active;
            }
        }
    }

    private subscribe()
    {
        if (!this.eventSubscription)
        {
            this.videoElement.addEventListener("playing", this.eventListeners.onVideoPlaying);
            this.videoElement.addEventListener("pause", this.eventListeners.onVideoPaused);
            this.videoElement.addEventListener("ended", this.eventListeners.onVideoPlayed);
            if (this.playButtonElement !== undefined)
            {
                this.playButtonElement.addEventListener("click", this.eventListeners.onPlayClick);
            }
            if (this.pauseButtonElement !== undefined)
            {
                this.pauseButtonElement.addEventListener("click", this.eventListeners.onPauseClick);
            }
            if (this.replayButtonElement !== undefined)
            {
                this.replayButtonElement.addEventListener("click", this.eventListeners.onPlayClick);
            }
            this.eventSubscription = true;
        }
    }

    private unsubscribe()
    {
        if (this.eventSubscription)
        {
            this.videoElement.removeEventListener("playing", this.eventListeners.onVideoPlaying);
            this.videoElement.removeEventListener("pause", this.eventListeners.onVideoPaused);
            this.videoElement.removeEventListener("ended", this.eventListeners.onVideoPlayed);
            if (this.playButtonElement !== undefined)
            {
                this.playButtonElement.removeEventListener("click", this.eventListeners.onPlayClick);
            }
            if (this.pauseButtonElement !== undefined)
            {
                this.pauseButtonElement.removeEventListener("click", this.eventListeners.onPauseClick);
            }
            if (this.replayButtonElement !== undefined)
            {
                this.replayButtonElement.removeEventListener("click", this.eventListeners.onPlayClick);
            }
            this.eventSubscription = false;
        }
    }
}